import {CreateSepaXmlByTimeRangeInput} from "../../../generated/graphql";

/**
 * Preview-Koordinator für den SEPA-XML-Export-Dialog.
 *
 * Hintergrund (Issue #671): Die Eingabefelder haben früher jeweils selbst Previews gefeuert
 * (useEffect beim Mount + onBlur + onOpenChange). Beim Öffnen des Dialogs liefen dadurch mehrere
 * identische `createSepaXmlByTimeRange`-Mutationen parallel — serverseitig sehr teuer
 * (Prod-RSS-Peak 2,58 GB).
 *
 * Dieser Koordinator ist bewusst frei von React, damit er direkt unit-testbar ist. Er garantiert:
 *  - Dedupe:        identischer (normalisierter) Input ⇒ kein erneuter Request.
 *  - Single-Flight: solange ein Request läuft, wird nur der NEUESTE abweichende Input vorgemerkt
 *                   und nach Abschluss genau einmal nachgeladen.
 *  - Staleness:     Antworten eines Requests, der zwischenzeitlich durch `reset()` entwertet wurde
 *                   (z.B. weil ein Write erfolgreich war), werden verworfen — weder `apply` noch
 *                   `onError` laufen dann noch.
 */

export type PreviewCoordinator = {
    /** Fordert eine Preview an (dedupliziert + single-flight). */
    request: (input: CreateSepaXmlByTimeRangeInput) => void;
    /**
     * Entwertet alle laufenden/vorgemerkten Previews. Danach kann derselbe Input erneut
     * angefordert werden (z.B. nach einem Write oder beim Schliessen des Dialogs).
     */
    reset: () => void;
};

type CreatePreviewCoordinatorArgs<TResult> = {
    run: (input: CreateSepaXmlByTimeRangeInput) => Promise<TResult>;
    apply: (result: TResult, input: CreateSepaXmlByTimeRangeInput) => void;
    onError?: (error: unknown) => void;
};

/**
 * Stabiler Dedupe-Key aus den fachlich relevanten (bereits normalisierten) Feldern.
 * `saveFile` gehört NICHT dazu: der Koordinator kennt ausschliesslich den Preview-Pfad.
 */
export const buildPreviewKey = (input: CreateSepaXmlByTimeRangeInput): string =>
    JSON.stringify([
        input.startDate || null,
        input.endDate || null,
        input.collectionDate || null,
        typeof input.maxAmount === "number" ? input.maxAmount : null,
        typeof input.maxMoneyAmount === "number" ? input.maxMoneyAmount : null,
    ]);

export const createPreviewCoordinator = <TResult>({run, apply, onError}: CreatePreviewCoordinatorArgs<TResult>): PreviewCoordinator => {
    // Key des zuletzt AKZEPTIERTEN Inputs (laufend oder vorgemerkt) — Basis für den Dedupe.
    let lastKey: string | undefined;
    let inFlight = false;
    let pending: CreateSepaXmlByTimeRangeInput | undefined;
    // Sequenznummer: nur die Antwort des jeweils aktuellsten Requests darf Zustand schreiben.
    let sequence = 0;

    const start = (input: CreateSepaXmlByTimeRangeInput): void => {
        inFlight = true;
        sequence = sequence + 1;
        const mySequence = sequence;

        const finish = (): void => {
            inFlight = false;
            const next = pending;

            pending = undefined;
            if (next) {
                start(next);
            }
        };

        run(input).then(
            (result) => {
                if (mySequence === sequence) {
                    apply(result, input);
                }
                finish();
            },
            (error) => {
                // Fehlgeschlagene Requests dürfen nicht dauerhaft "wegdedupliziert" werden.
                lastKey = undefined;
                if (mySequence === sequence && onError) {
                    onError(error);
                }
                finish();
            },
        );
    };

    return {
        request: (input) => {
            const key = buildPreviewKey(input);

            if (key === lastKey) {
                return;
            }
            lastKey = key;

            if (inFlight) {
                pending = input;

                return;
            }
            start(input);
        },
        reset: () => {
            lastKey = undefined;
            pending = undefined;
            sequence = sequence + 1;
        },
    };
};
